import { Flex } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppSelector } from '@/app/hooks';
import { captureException } from '@sentry/react';
import Modal from '@/components/Modals/Modal';
import dayjs from 'dayjs';
import { useSmallScreen } from '@/hooks/useWindowDimension';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import {
  useWorklogSubmit,
  type WorklogFormProps,
} from './hooks/useWorklogSubmit.tsx';
import { useWorklogAnalytics } from './hooks/useWorklogAnalytics';
import { useUnsavedChanges } from './hooks/useUnsavedChanges';
import { WorklogStatusSelector } from './components/WorklogStatusSelector';
import { WorklogFormFields } from './components/WorklogFormFields';
import { WorklogModalMode } from '@/features/worklogModalSlice.ts';

const NOTE_CHAR_LIMIT = 250;

export const WorklogAddOrEdit = ({ planId }: { planId?: string }) => {
  const {
    isOpened: openModal,
    worklogData: data,
    mode,
    spanId,
    spanCount,
  } = useAppSelector((s) => s.worklogModal);

  const isSmallScreen = useSmallScreen();
  const intl = useClientTerminology();
  const isBulkMode = mode === WorklogModalMode.BULK_ADD;
  const isEditMode = mode === WorklogModalMode.EDIT;

  const initialFormValues = useMemo<WorklogFormProps>(() => {
    if (!data) {
      return {
        date: new Date(),
        entity_type: 'span',
        notes: '',
        entity_id: spanId ?? '',
        status: undefined,
      };
    }
    return {
      ...data,
      notes: data.notes ?? '',
      date: dayjs.utc(data.date).toDate(),
    };
  }, [data, spanId]);

  const form = useForm<WorklogFormProps>({
    initialValues: initialFormValues,
    validate: (values) => ({
      notes:
        values.notes && values.notes.length > NOTE_CHAR_LIMIT
          ? 'Text too long'
          : null,
    }),
  });

  // Reset form when modal opens or data changes
  useEffect(() => {
    if (openModal) {
      form.setValues(initialFormValues);
      form.resetDirty(initialFormValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, initialFormValues]);

  const { submitWorklog, isLoading } = useWorklogSubmit({ planId });

  const { trackWorklogEvent } = useWorklogAnalytics({
    mode: mode ?? WorklogModalMode.ADD,
  });

  const handleClose = useUnsavedChanges({ form });

  const submitValues = useCallback(
    async (values: WorklogFormProps) => {
      if (!form.isValid()) {
        return;
      }

      await submitWorklog({
        values,
        worklogId: data?.id,
        isDirty: form.isDirty(),
      });

      // Track analytics after successful submission
      trackWorklogEvent(values, data ?? undefined);
    },
    [form, submitWorklog, data, trackWorklogEvent]
  );
  const handleFormSubmit = form.onSubmit(async (values) => {
    try {
      await submitValues(values);
    } catch (error) {
      captureException(error);
    }
  });

  const modalTitle = useMemo(() => {
    if (isEditMode) {
      return intl.formatMessage({ id: 'worklog.editActivity' });
    }

    if (isBulkMode && spanCount !== undefined) {
      return `${intl.formatMessage({ id: 'worklog.addActivity' })} to ${intl.formatNumber(spanCount)} span${spanCount === 1 ? '' : 's'}`;
    }

    return intl.formatMessage({ id: 'worklog.addActivity' });
  }, [isEditMode, isBulkMode, spanCount, intl]);

  return (
    <Modal
      testId="worklog-modal"
      primaryButtonTestId="worklog-confirm-button"
      secondaryButtonTestId="worklog-cancel-button"
      opened={openModal ?? false}
      onClose={handleClose}
      fullScreen={isSmallScreen}
      title={modalTitle}
      size={isSmallScreen ? '100%' : '500px'}
      formToSubmit="worklog-action-form"
      primaryLabel={intl.formatMessage({ id: 'action.confirm' })}
      isLoading={isLoading}
      primaryButtonDisabled={!form.values.status || !form.isValid()}
      onSecondaryAction={handleClose}
      secondaryLabel={intl.formatMessage({ id: 'action.cancel' })}
    >
      <form id="worklog-action-form" onSubmit={handleFormSubmit}>
        <WorklogStatusSelector
          value={form.values.status}
          onChange={(value) => form.setFieldValue('status', value)}
        />
        {form.values.status && (
          <WorklogFormFields
            status={form.values.status}
            dateValue={form.values.date}
            // Clearing the field leaves the previous date, because a work log
            // always carries one.
            onDateChange={(date) => date && form.setFieldValue('date', date)}
            notesValue={form.values.notes || ''}
            onNotesChange={(e) => form.setFieldValue('notes', e.target.value)}
            notesError={
              form.values.notes &&
              form.values.notes.length > NOTE_CHAR_LIMIT ? (
                <Flex justify="space-between">
                  <span>The text you have entered is too long</span>
                  <span>
                    {form.values.notes.length}/{NOTE_CHAR_LIMIT}
                  </span>
                </Flex>
              ) : undefined
            }
          />
        )}
      </form>
    </Modal>
  );
};
